import React from "react";
import { Plus, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Checkbox } from "@cms/ui/components/checkbox";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@cms/ui/components/select";
import { EVENT_WEEKDAYS, type EventRecurrence, type EventWeekday } from "../lib/events";

const WEEKDAY_NAMES: Record<EventWeekday, string> = {
  MO: "Monday", TU: "Tuesday", WE: "Wednesday", TH: "Thursday",
  FR: "Friday", SA: "Saturday", SU: "Sunday",
};

const ORDINALS = ["", "1st", "2nd", "3rd", "4th"];

/** The weekday code of a YYYY-MM-DD date (defaults MO on bad input). */
export function weekdayOf(date: string): EventWeekday {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "MO";
  return (["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as EventWeekday[])[parsed.getDay()];
}

/**
 * The monthly options the base date allows: its day of month, its nth-weekday
 * position, and — when it falls in the month's final week — "the last <day>".
 */
function monthlyOptions(date: string) {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return [];
  const day = parsed.getDate();
  const weekday = weekdayOf(date);
  const nth = Math.ceil(day / 7);
  const daysInMonth = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0).getDate();

  const options: { key: string; label: string; mode: "day_of_month" | "nth_weekday"; nth?: number }[] = [
    { key: "day_of_month", label: `Monthly on day ${day}`, mode: "day_of_month" },
  ];
  if (nth <= 4) {
    options.push({ key: "nth", label: `Monthly on the ${ORDINALS[nth]} ${WEEKDAY_NAMES[weekday]}`, mode: "nth_weekday", nth });
  }
  if (day > daysInMonth - 7) {
    options.push({ key: "last", label: `Monthly on the last ${WEEKDAY_NAMES[weekday]}`, mode: "nth_weekday", nth: -1 });
  }
  return options;
}

/**
 * Recurrence builder for the event editor: frequency/interval, weekly day
 * picker, monthly relative/absolute mode, series end, and exception date
 * ranges. `value === null` means "doesn't repeat"; the parent owns the state
 * and receives every change as a full recurrence object (docs/events-module.md).
 */
export function EventRecurrenceCard({
  value,
  baseDate,
  onChange,
}: {
  value: EventRecurrence | null;
  baseDate: string;
  onChange: (value: EventRecurrence | null) => void;
}) {
  const enable = () =>
    onChange({
      freq: "week",
      interval: 1,
      by_weekday: [weekdayOf(baseDate)],
      ends: { type: "never" },
      exceptions: [],
    });

  const update = (patch: Partial<EventRecurrence>) => {
    if (!value) return;
    const next = { ...value, ...patch };
    // Keep only the fields the frequency uses, and reseed weekly/monthly
    // defaults from the base date when the frequency changes.
    if (next.freq === "week") {
      next.by_weekday = next.by_weekday?.length ? next.by_weekday : [weekdayOf(baseDate)];
    } else {
      delete next.by_weekday;
    }
    if (next.freq === "month") {
      next.monthly_mode = next.monthly_mode ?? "day_of_month";
      if (next.monthly_mode === "nth_weekday") {
        next.nth = next.nth ?? Math.ceil(new Date(`${baseDate}T00:00:00`).getDate() / 7);
        next.weekday = next.weekday ?? weekdayOf(baseDate);
      }
    } else {
      delete next.monthly_mode;
      delete next.nth;
      delete next.weekday;
    }
    onChange(next);
  };

  const toggleWeekday = (code: EventWeekday) => {
    if (!value) return;
    const current = value.by_weekday ?? [];
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    if (next.length === 0) return; // at least one day stays selected
    update({ by_weekday: EVENT_WEEKDAYS.map((d) => d.code).filter((c) => next.includes(c)) });
  };

  const monthly = value?.freq === "month" ? monthlyOptions(baseDate) : [];
  const monthlyKey =
    value?.monthly_mode === "nth_weekday" ? (value.nth === -1 ? "last" : "nth") : "day_of_month";

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Repeats</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={!!value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => (e.target.checked ? enable() : onChange(null))}
          />
          This event repeats
        </label>

        {value && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm">Repeat every</span>
              <Input
                type="number"
                min={1}
                max={99}
                value={value.interval}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update({ interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })
                }
                className="w-20"
                aria-label="Repeat interval"
              />
              <Select value={value.freq} onValueChange={(freq: EventRecurrence["freq"]) => update({ freq })}>
                <SelectTrigger className="w-32" aria-label="Repeat frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">{value.interval === 1 ? "day" : "days"}</SelectItem>
                  <SelectItem value="week">{value.interval === 1 ? "week" : "weeks"}</SelectItem>
                  <SelectItem value="month">{value.interval === 1 ? "month" : "months"}</SelectItem>
                  <SelectItem value="year">{value.interval === 1 ? "year" : "years"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {value.freq === "week" && (
              <div className="flex flex-wrap gap-1.5">
                {EVENT_WEEKDAYS.map(({ code, label }) => {
                  const selected = value.by_weekday?.includes(code);
                  return (
                    <Button
                      key={code}
                      type="button"
                      variant={selected ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleWeekday(code)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            )}

            {value.freq === "month" && monthly.length > 0 && (
              <Select
                value={monthlyKey}
                onValueChange={(key: string) => {
                  const option = monthly.find((o) => o.key === key);
                  if (!option) return;
                  update(
                    option.mode === "day_of_month"
                      ? { monthly_mode: "day_of_month", nth: undefined, weekday: undefined }
                      : { monthly_mode: "nth_weekday", nth: option.nth, weekday: weekdayOf(baseDate) }
                  );
                }}
              >
                <SelectTrigger aria-label="Monthly repeat mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthly.map((option) => (
                    <SelectItem key={option.key} value={option.key}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="space-y-2">
              <Label>Ends</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={value.ends.type}
                  onValueChange={(type: "never" | "on" | "count") =>
                    update({
                      ends:
                        type === "on"
                          ? { type, date: value.ends.date ?? baseDate }
                          : type === "count"
                            ? { type, n: value.ends.n ?? 10 }
                            : { type: "never" },
                    })
                  }
                >
                  <SelectTrigger className="w-44" aria-label="Series end">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never</SelectItem>
                    <SelectItem value="on">On a date</SelectItem>
                    <SelectItem value="count">After occurrences</SelectItem>
                  </SelectContent>
                </Select>
                {value.ends.type === "on" && (
                  <Input
                    type="date"
                    value={value.ends.date ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      update({ ends: { type: "on", date: e.target.value } })
                    }
                    className="w-44"
                    aria-label="Series end date"
                  />
                )}
                {value.ends.type === "count" && (
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    value={value.ends.n ?? 10}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      update({ ends: { type: "count", n: Math.max(1, Math.min(500, Number(e.target.value) || 1)) } })
                    }
                    className="w-24"
                    aria-label="Number of occurrences"
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Skip dates</Label>
              <p className="text-xs text-muted-foreground">
                Dates or ranges where this event doesn't take place (holidays, closures).
              </p>
              {value.exceptions.map((exception, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={exception.from}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const exceptions = value.exceptions.slice();
                      const from = e.target.value;
                      exceptions[index] = { from, to: exception.to && exception.to >= from ? exception.to : from };
                      update({ exceptions });
                    }}
                    className="w-40"
                    aria-label="Skip from"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={exception.to}
                    min={exception.from}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const exceptions = value.exceptions.slice();
                      exceptions[index] = { ...exception, to: e.target.value };
                      update({ exceptions });
                    }}
                    className="w-40"
                    aria-label="Skip until"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => update({ exceptions: value.exceptions.filter((_, i) => i !== index) })}
                    aria-label="Remove skipped dates"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => update({ exceptions: [...value.exceptions, { from: baseDate, to: baseDate }] })}
                disabled={value.exceptions.length >= 50}
              >
                <Plus className="h-3.5 w-3.5" />
                Add skipped dates
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
